import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle, Plus, Sparkle } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DateField, TimeField } from "@/components/DateTimeField";
import { CrewSelect } from "@/components/CrewSelect";
import { TaskDialog } from "@/components/TaskDialog";
import { TemplateChecklistSheet } from "@/components/TemplateChecklistSheet";
import { ExtraStopCard } from "@/components/ExtraStopCard";
import { ARRIVAL_WINDOWS } from "@/lib/scheduleWindows";
import { api, formatApiErrorDetail } from "@/lib/api";

const STAGES = [
  { key: "template", label: "Template", color: "#a78bfa", stop: "Template" },
  { key: "fab", label: "Fabrication", color: "#facc15", stop: "Fabrication" },
  { key: "install", label: "Install", color: "#60a5fa", stop: "Install" },
];
const WINDOWS = ["All day", "Morning", "Afternoon", "Evening"];
const STAGE_TYPE_IDS = new Set(["templating", "fabrication", "install"]);

const fromJob = (job) => {
  const f = {};
  STAGES.forEach(({ key }) => {
    f[`${key}_date`] = job?.[`${key}_date`] || "";
    f[`${key}_time`] = job?.[`${key}_time`] || "";
    f[`${key}_window`] = job?.[`${key}_window`] || "All day";
    f[`${key}_crew`] = job?.[`${key}_crew`] || "";
  });
  return f;
};

export function WorkflowDialog({ open, onOpenChange, job, crewList, onSaved, focusStage = "" }) {
  const [f, setF] = useState(fromJob(job));
  const [saving, setSaving] = useState(false);
  const [ai, setAi] = useState(null); // {proposal, rationale, risks, source}
  const [aiBusy, setAiBusy] = useState(false);
  const [aiNotes, setAiNotes] = useState("");
  const [stops, setStops] = useState(job?.stop_status || {});
  const [marking, setMarking] = useState("");
  const [extra, setExtra] = useState(job?.extra_stops || []);
  const [taskTypes, setTaskTypes] = useState([]);
  const [stageDialog, setStageDialog] = useState({ open: false, task: null });
  const [checklistOpen, setChecklistOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setStops(job?.stop_status || {});
      setExtra(job?.extra_stops || []);
    }
  }, [open, job]);

  useEffect(() => {
    if (!open || taskTypes.length) return;
    api
      .get("/settings/task-types")
      .then(({ data }) => setTaskTypes((data.task_types || []).filter((t) => STAGE_TYPE_IDS.has(t.id))))
      .catch(() => {});
  }, [open, taskTypes.length]);

  const stageChanged = (data) => {
    setExtra(data.extra_stops || []);
    setStops(data.stop_status || {});
    onSaved?.(data);
  };
  const deleteStage = async (sid) => {
    try {
      const { data } = await api.delete(`/jobs/${job.id}/extra-stops/${sid}`);
      stageChanged(data);
      toast.success("Stage removed");
    } catch {
      toast.error("Could not remove the stage");
    }
  };

  // a stage can be closed out even if it was never put on the calendar
  const markStop = async (stop, done) => {
    setMarking(stop);
    try {
      const { data } = await api.patch(`/jobs/${job.id}/stops/${stop}`, { status: done ? "Done" : "Pending" });
      setStops(data.stop_status || {});
      onSaved?.(data);
      toast.success(done ? `${stop} marked complete` : `${stop} reopened`);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (stop === "Template" && d && typeof d === "object" && d.code === "checklist_incomplete") {
        setChecklistOpen(true);
      } else {
        toast.error((d && typeof d === "object" && d.message) || formatApiErrorDetail(d) || "Could not update the stage");
      }
    } finally {
      setMarking("");
    }
  };

  useEffect(() => {
    if (open) {
      setF(fromJob(job));
      setAi(null);
    }
  }, [open, job]);

  useEffect(() => {
    if (!open || !focusStage) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="workflow-stage-${focusStage}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-indigo-400/70");
      el.querySelector("input, button, select")?.focus?.({ preventScroll: true });
      setTimeout(() => el.classList.remove("ring-2", "ring-indigo-400/70"), 1800);
    }, 250);
    return () => clearTimeout(t);
  }, [open, focusStage]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const suggest = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/jobs/${job.id}/ai-workflow`, { notes: aiNotes });
      setAi(data);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not build a workflow suggestion",
      );
    } finally {
      setAiBusy(false);
    }
  };
  const applyAi = () => {
    if (!ai?.proposal) return;
    setF((p) => ({ ...p, ...ai.proposal }));
    toast.success("Suggestion applied — review, then save");
  };
  const fmtDay = (d) =>
    d
      ? new Date(`${d}T12:00:00`).toLocaleDateString(undefined, {
          weekday: "short",
          day: "numeric",
          month: "short",
        })
      : "—";

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/jobs/${job.id}`, f);
      onSaved?.(data);
      onOpenChange(false);
      toast.success("Workflow updated");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not save the workflow",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        data-testid="workflow-dialog"
      >
        <DialogHeader>
          <DialogTitle>Workflow</DialogTitle>
        </DialogHeader>
        <p className="-mt-2 text-xs text-muted-foreground">
          Schedule each stage and assign a crew member or a whole team.
        </p>
        <div
          className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3"
          data-testid="workflow-ai"
        >
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-amber-200">
              <Sparkle size={14} weight="fill" /> AI scheduler
            </div>
            <input
              data-testid="workflow-ai-notes"
              value={aiNotes}
              onChange={(e) => setAiNotes(e.target.value)}
              placeholder="Anything to consider? e.g. install before the 20th"
              className="h-8 min-w-[10rem] flex-1 rounded border border-border bg-background px-2 text-xs text-foreground outline-none"
            />
            <Button
              type="button"
              size="sm"
              data-testid="workflow-ai-suggest"
              onClick={suggest}
              disabled={aiBusy}
              className="h-8 bg-indigo-500 hover:bg-indigo-400"
            >
              {aiBusy ? "Planning…" : ai ? "Re-plan" : "Suggest workflow"}
            </Button>
          </div>
          {ai?.proposal && (
            <div className="mt-3 space-y-2" data-testid="workflow-ai-proposal">
              <div className="grid gap-1.5 sm:grid-cols-3">
                {STAGES.map((s) => (
                  <div
                    key={s.key}
                    data-testid={`workflow-ai-${s.key}`}
                    className="rounded border border-border bg-background px-2 py-1.5 text-xs"
                  >
                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                      {s.label}
                    </div>
                    <div className="mt-0.5 font-semibold text-foreground">
                      {fmtDay(ai.proposal[`${s.key}_date`])}
                      <span className="font-normal text-muted-foreground"> · {ai.proposal[`${s.key}_window`]}</span>
                    </div>
                    <div className="text-muted-foreground">{ai.proposal[`${s.key}_crew`] || "Unassigned"}</div>
                  </div>
                ))}
              </div>
              {ai.rationale?.length > 0 && (
                <ul className="list-disc space-y-0.5 pl-4 text-xs text-zinc-300" data-testid="workflow-ai-rationale">
                  {ai.rationale.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              )}
              {ai.risks?.length > 0 && (
                <ul className="space-y-0.5 text-xs text-amber-200" data-testid="workflow-ai-risks">
                  {ai.risks.map((r, i) => (
                    <li key={i}>⚠ {r}</li>
                  ))}
                </ul>
              )}
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500" data-testid="workflow-ai-source">
                  {ai.source === "ai" ? "Planned by AI from the shop calendar" : "Rule-based plan (AI unavailable)"}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  data-testid="workflow-ai-apply"
                  onClick={applyAi}
                  className="h-8 border-indigo-400/50 text-indigo-100"
                >
                  Apply to form
                </Button>
              </div>
            </div>
          )}
        </div>
        <div className="space-y-4">
          {STAGES.map((s) => (
            <div
              key={s.key}
              data-testid={`workflow-stage-${s.key}`}
              className="rounded-lg border border-border bg-background p-3"
            >
              <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-foreground">
                {s.label}
                {stops[s.stop]?.status === "Done" && (
                  <span data-testid={`workflow-${s.key}-done-badge`} className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] text-emerald-300 ring-1 ring-emerald-400/40">
                    Complete
                  </span>
                )}
                <button
                  type="button"
                  data-testid={`workflow-${s.key}-mark-complete`}
                  disabled={marking === s.stop}
                  onClick={() => markStop(s.stop, stops[s.stop]?.status !== "Done")}
                  title={f[`${s.key}_date`] ? "" : "No date needed — closes this stage as-is"}
                  className={`ml-auto flex items-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 disabled:opacity-50 ${
                    stops[s.stop]?.status === "Done"
                      ? "border-zinc-700 text-zinc-400 hover:bg-zinc-800"
                      : "border-emerald-400/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
                  }`}
                >
                  <CheckCircle size={12} weight="bold" />
                  {stops[s.stop]?.status === "Done" ? "Reopen" : "Mark complete"}
                </button>
              </div>
              {stops[s.stop]?.status === "Done" && (
                <div data-testid={`workflow-${s.key}-done-stamp`} className="-mt-1 mb-2 text-[11px] text-emerald-300/80">
                  Completed{" "}
                  {stops[s.stop].done_at
                    ? new Date(stops[s.stop].done_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
                    : ""}
                  {stops[s.stop].done_by ? ` by ${stops[s.stop].done_by}` : ""}
                </div>
              )}
              <div className="grid gap-2 sm:grid-cols-2">
                <div>
                  <div className="mb-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                    Date
                  </div>
                  <DateField
                    testId={`workflow-${s.key}-date`}
                    value={f[`${s.key}_date`]}
                    onChange={(v) => set(`${s.key}_date`, v)}
                  />
                </div>
                <div>
                  <div className="mb-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                    Window
                  </div>
                  <select
                    data-testid={`workflow-${s.key}-window`}
                    value={f[`${s.key}_window`]}
                    onChange={(e) => set(`${s.key}_window`, e.target.value)}
                    className="h-10 w-full rounded border border-border bg-background px-2 text-sm text-foreground"
                  >
                    {[...new Set([f[`${s.key}_window`] || "All day", ...ARRIVAL_WINDOWS, ...WINDOWS])].map((w) => (
                      <option key={w} value={w}>
                        {w}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="mb-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                    Time (optional)
                  </div>
                  <TimeField
                    testId={`workflow-${s.key}-time`}
                    value={f[`${s.key}_time`]}
                    onChange={(v) => set(`${s.key}_time`, v)}
                  />
                </div>
                <div>
                  <div className="mb-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                    Crew
                  </div>
                  <CrewSelect
                    testid={`workflow-${s.key}-crew`}
                    value={f[`${s.key}_crew`]}
                    onChange={(v) => set(`${s.key}_crew`, v)}
                    crewList={crewList}
                  />
                </div>
              </div>
            </div>
          ))}
          <div data-testid="workflow-extra-stages" className="rounded-lg border border-dashed border-border bg-background/60 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-widest text-foreground">Additional stages</span>
              <button
                type="button"
                data-testid="workflow-add-stage"
                onClick={() => setStageDialog({ open: true, task: null })}
                className="inline-flex items-center gap-1 rounded-full border border-indigo-400/50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-indigo-200 hover:bg-indigo-500/15"
              >
                <Plus size={12} weight="bold" /> Add stage
              </button>
            </div>
            {extra.length === 0 ? (
              <p className="text-xs text-muted-foreground">Need a second template, fab run or install for other areas? Add it here.</p>
            ) : (
              <div className="space-y-2">
                {extra.map((st) => (
                  <ExtraStopCard
                    key={st.id}
                    jobId={job.id}
                    stop={st}
                    onChanged={stageChanged}
                    onEdit={(s) => setStageDialog({ open: true, task: s })}
                    onDelete={deleteStage}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
        {job && (
          <TaskDialog
            open={stageDialog.open}
            onOpenChange={(v) => setStageDialog((s) => ({ ...s, open: v }))}
            resourcePath={`/jobs/${job.id}/extra-stops`}
            areasSource={`/jobs/${job.id}/drawing-areas`}
            task={stageDialog.task}
            taskTypes={taskTypes}
            crewList={crewList}
            noun="stage"
            onSaved={stageChanged}
          />
        )}
        {checklistOpen && job && (
          <TemplateChecklistSheet
            jobId={job.id}
            code={job.code}
            onClose={() => setChecklistOpen(false)}
            onComplete={async () => {
              await markStop("Template", true);
            }}
          />
        )}
        <div className="mt-2 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid="workflow-cancel"
            onClick={() => onOpenChange(false)}
            className="border-zinc-700"
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="workflow-save"
            onClick={save}
            disabled={saving}
            className="bg-amber-500 text-black hover:bg-amber-400"
          >
            Save workflow
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
